import { Button, SubsectionTitle } from '@sleepstation/components'
import { CopyParagraph } from '@sleepstation/components-copy'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import {
  DIRECTION,
  LAYOUT_ALIGN,
  LAYOUT_SPACING,
  SIZE,
} from '@sleepstation/const'
import { color, marginTop } from '@sleepstation/style-api-helpers'
import PropTypes from 'prop-types'
import { concat, ifElse, isEmpty, without } from 'ramda'
import { isNotEmpty } from 'ramda-adjunct'
import React, { useState } from 'react'
import styled from 'styled-components'
import BreakoutModuleNarrower from '../../../shared/breakoutModules/BreakoutModuleNarrower'
import InterestsList from './InterestsList'

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const handleSubmitInterests =
  (onSubmitInterests, selectedInterests, setError, content) => () => {
    if (isNotEmpty(selectedInterests)) {
      // Pass interests to API then …
      onSubmitInterests(selectedInterests)
    } else {
      setError(content.selectionError.noneSelected)
    }
  }

const updateSelectedInterests =
  (setSelectedInterests, selectedInterests) =>
  (id, { isSelected }) => {
    const updatedSelectedInterests = ifElse(
      () => isSelected,
      concat,
      without
    )([id], selectedInterests)
    setSelectedInterests(updatedSelectedInterests)
  }

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled(VLayout).attrs({
  align: LAYOUT_ALIGN.STRETCH,
  spacing: [LAYOUT_SPACING.LARGE],
})``

const ErrorMessage = styled(CopyParagraph).attrs({
  isLede: true,
  'data-cy': `interests-error-message`,
})`
  ${color(`neutral`)}
`

const Header = styled.div`
  margin: auto;
`

const Body = styled(VLayout).attrs({
  spacing: [LAYOUT_SPACING.MEDIUM],
  align: LAYOUT_ALIGN.STRETCH,
})`
  > *:last-child {
    ${marginTop(1.25)}
  }
`

const Footer = styled(BreakingLayout).attrs({
  direction: [DIRECTION.VERTICAL],
  align: [LAYOUT_ALIGN.CENTER],
})`
  ${marginTop([2, 2.5])}

  > * {
    width: 100%;
    max-width: 346px;
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const OnboardingInterestsPage = ({ pageContent, onSubmitInterests }) => {
  const [selectedInterests, setSelectedInterests] = useState([])
  const [error, setError] = useState()

  const shouldShowError = error && isEmpty(selectedInterests)

  return (
    <BreakoutModuleNarrower
      hasBottomBorder={false}
      hasGradient={false}
      contentIsCropped={false}
      marginV={[
        [1.5, 2],
        [1.5, 3],
        [2.5, 4],
      ]}
    >
      <Layout>
        <Header>
          <SubsectionTitle>{pageContent.title}</SubsectionTitle>
        </Header>
        <Body>
          {shouldShowError && <ErrorMessage>{error}</ErrorMessage>}
          <CopyParagraph>{pageContent.listPrompt}</CopyParagraph>
          <InterestsList
            interests={pageContent.interests}
            selectedInterests={selectedInterests}
            onSelectionChange={updateSelectedInterests(
              setSelectedInterests,
              selectedInterests
            )}
          />
        </Body>
        <Footer>
          <Button
            data-cy="interests-submit-button"
            onClick={handleSubmitInterests(
              onSubmitInterests,
              selectedInterests,
              setError,
              pageContent
            )}
            label={pageContent.continueButtonLabel}
            size={SIZE.SMALL}
          />
        </Footer>
      </Layout>
    </BreakoutModuleNarrower>
  )
}

OnboardingInterestsPage.propTypes = {
  pageContent: PropTypes.object.isRequired,
  onSubmitInterests: PropTypes.func.isRequired,
}

export default OnboardingInterestsPage
